import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { Package, FileText, Plus, Trash2, ShoppingCart, Download, Save, X, Home } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import TemplateManager from './TemplateManager';


// Progress Bar Component
function ProgressBar({ progress, message }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-80">
        <div className="text-center">
          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="text-yellow-600" size={24} />
          </div>
          <h3 className="text-lg font-semibold mb-2">Generating Quote</h3>
          <p className="text-sm text-gray-600 mb-4">{message}</p>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div 
              className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          <div className="text-xs text-gray-500">
            {progress < 30 && "Preparing quote data..."}
            {progress >= 30 && progress < 60 && "Generating PDF..."}
            {progress >= 60 && progress < 90 && "Sending emails..."}
            {progress >= 90 && "Finalizing..."}
          </div>
        </div>
      </div>
    </div>
  );
}

// Success Modal Component
function SuccessModal({ isOpen, onClose, onDownload, onHome, quoteId, hasDownloadPermission }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={20} />
        </button>

        <div className="text-center">
          {/* Success Icon */}
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mb-2">Quote Generated Successfully!</h3>
          
          <div className="text-sm text-gray-600 mb-6 space-y-2">
            <p>Quote ID: <span className="font-semibold">{quoteId}</span></p>
            <p>✓ Confirmation email sent to the client</p>
            <p>✓ Approval request sent to quote approver</p>
          </div>

          <div className="flex flex-col space-y-3">
            {hasDownloadPermission && (
              <Button
                onClick={onDownload}
                className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-semibold"
              >
                <Download className="mr-2" size={16} />
                Download Quote
              </Button>
            )}
            
            <Button
              onClick={onHome}
              variant="outline"
              className="border-slate-300 hover:bg-slate-50"
            >
              <Home className="mr-2" size={16} />
              Home
            </Button>
            
            <Button
			  onClick={onDownload}
			  className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-semibold"
			>
			  <Download className="mr-2" size={16} />
			  Download Quote
			</Button>

			<Button
			  onClick={() => window.location.href = "/quotes"}
			  variant="outline"
			  className="border-slate-300 hover:bg-slate-50"
			>
			  <Home className="mr-2" size={16} />
			  Generate Another Quote
			</Button>

			<Button
			  onClick={onClose}
			  variant="ghost"
			  className="text-slate-600 hover:text-slate-800 hover:bg-slate-100"
			>
			  Close
			</Button>

          </div>
        </div>
      </div>
    </div>
  );
}

// Quote Generator Page with Two Systems
function QuotePage() {
  const { hasPermission, user } = useAuth();
  const [activeQuoteType, setActiveQuoteType] = useState('catalogue');
  
  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold text-center mb-12 text-slate-800">Generate Quote</h1>
          
          {/* Quote Type Selection */}
          {/* Quote Type Selection */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg p-2 shadow-lg">
              <Button
                onClick={() => setActiveQuoteType('catalogue')}
                className={`mr-2 ${activeQuoteType === 'catalogue' 
                  ? 'bg-yellow-400 text-slate-900' 
                  : 'bg-transparent text-slate-600 hover:bg-slate-100'}`}
              >
                <Package className="mr-2" size={16} />
                Catalogue Quote
              </Button>

              {hasPermission('generate_manual_quote') && (
                <Button
                  onClick={() => setActiveQuoteType('manual')}
                  className={`mr-2 ${activeQuoteType === 'manual' 
                    ? 'bg-yellow-400 text-slate-900' 
                    : 'bg-transparent text-slate-600 hover:bg-slate-100'}`}
                >
                  <FileText className="mr-2" size={16} />
                  Manual Quote
                </Button>
              )}

              {hasPermission('generate_individual_item_quote') && (
                <Button
                  onClick={() => setActiveQuoteType('individual')}
                  className={`${activeQuoteType === 'individual' 
                    ? 'bg-yellow-400 text-slate-900' 
                    : 'bg-transparent text-slate-600 hover:bg-slate-100'}`}
                >
                  <ShoppingCart className="mr-2" size={16} />
                  Individual Item Quote
                </Button>
              )}
            </div>
          </div>


          {activeQuoteType === 'catalogue' && <CatalogueQuoteForm />}

          {activeQuoteType === 'manual' && hasPermission('generate_manual_quote') && (
            <ManualQuoteForm />
          )}

          {activeQuoteType === 'individual' && hasPermission('generate_individual_item_quote') && (
            <IndividualItemQuoteForm />
          )}

        </div>
      </div>
    </div>
  );
}

// Catalogue Quote Form Component
function CatalogueQuoteForm() {
  const { isAdmin, hasPermission } = useAuth();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [quoteData, setQuoteData] = useState({
    company_name: '',
    client_email: '',
    client_address: '',
    subject: '',
    salutation: '',
    quote_description: '',
    pf_costs: 0,
    freight_charges: 0, // NEW FIELD
    tax_type: 'GST', // NEW FIELD
    gst_percentage: 18,
    quote_date: new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).replace(/ /g, '-'),
    terms_conditions: '',
    signature: '',
    bank_details: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [generatedQuoteId, setGeneratedQuoteId] = useState('');
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  
  // New states for progress and modal
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await apiClient.get(`/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleProductSelect = (product, quantity) => {
    const updatedProducts = [...selectedProducts];
    const existingIndex = updatedProducts.findIndex(p => p.product_id === product.product_id);
    
    if (existingIndex >= 0) {
      if (quantity > 0) {
        updatedProducts[existingIndex] = {
          ...updatedProducts[existingIndex],
          quantity: quantity
        };
      } else {
        updatedProducts.splice(existingIndex, 1);
      }
    } else if (quantity > 0) {
      updatedProducts.push({ ...product, quantity });
    }
    
    setSelectedProducts(updatedProducts);
  };

  const calculateTotals = () => {
    const itemsTotal = selectedProducts.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalBeforeTax = itemsTotal + quoteData.pf_costs + quoteData.freight_charges; // UPDATED
    const taxAmount = totalBeforeTax * (quoteData.gst_percentage / 100);
    const totalAfterTax = totalBeforeTax + taxAmount;

    return { itemsTotal, totalBeforeTax, taxAmount, totalAfterTax };
  };


  const simulateProgress = () => {
    setProgress(0);
    const intervals = [
      { time: 500, progress: 20, message: "Preparing quote data..." },
      { time: 1000, progress: 40, message: "Generating PDF document..." },
      { time: 1500, progress: 70, message: "Sending confirmation email..." },
      { time: 2000, progress: 85, message: "Sending approval request..." },
      { time: 500, progress: 100, message: "Finalizing quote..." }
    ];

    intervals.forEach((interval, index) => {
      setTimeout(() => {
        setProgress(interval.progress);
        setProgressMessage(interval.message);
        
        if (index === intervals.length - 1) {
          setTimeout(() => {
            setShowProgress(false);
            setShowSuccessModal(true);
          }, 500);
        }
      }, interval.time);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedProducts.length === 0) {
      setMessage('Please select at least one product.');
      return;
    }

    setLoading(true);
    setShowProgress(true);
    simulateProgress();
    
    try {
      const formData = new FormData();
      formData.append('company_name', quoteData.company_name);
      formData.append('client_email', quoteData.client_email);
      formData.append('client_address', quoteData.client_address);
      formData.append('subject', quoteData.subject);
      formData.append('salutation', quoteData.salutation);
      formData.append('quote_description', quoteData.quote_description);
      formData.append('pf_costs', quoteData.pf_costs);
	  formData.append('freight_charges', quoteData.freight_charges); // ADD THIS LINE
	  formData.append('tax_type', quoteData.tax_type);
      formData.append('gst_percentage', quoteData.gst_percentage);
      formData.append('quote_date', quoteData.quote_date);
      formData.append('terms_conditions', quoteData.terms_conditions);
      formData.append('signature', quoteData.signature);
      formData.append('bank_details', quoteData.bank_details);
      
      // Add catalogue items and quantities as arrays
      selectedProducts.forEach(product => {
        formData.append('catalogue_items[]', product.product_id);
        formData.append('item_quantities[]', product.quantity.toString());
      });

      const response = await apiClient.post(`/quotes/catalogue`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setGeneratedQuoteId(response.data.quote_id);
      
      // Reset form
      setQuoteData({
        company_name: '',
        client_email: '',
        client_address: '',
        subject: '',
        salutation: '',
        quote_description: '',
        pf_costs: 0,
		freight_charges: 0, // RESET THIS TOO
        tax_type: 'GST',
        gst_percentage: 18,
        terms_conditions: '',
        signature: '',
        bank_details: ''
      });
      setSelectedProducts([]);
    } catch (error) {
      setMessage('Error generating quote. Please try again.');
      setShowProgress(false);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!generatedQuoteId) return;
    
    try {
      const response = await apiClient.get(`/quotes/${generatedQuoteId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote_${generatedQuoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Close modal after download
      setShowSuccessModal(false);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleHome = () => {
    // Navigate to home/dashboard
    window.location.href = '/dashboard'; // Adjust to your home route
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const handleTemplateSelect = (template) => {
    setQuoteData({
      ...quoteData,
      terms_conditions: template.terms_conditions,
      signature: template.signature,
      bank_details: template.bank_details
    });
    setShowTemplateManager(false);
  };

  const { itemsTotal, totalBeforeTax, taxAmount, totalAfterTax } = calculateTotals();

  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      {showProgress && <ProgressBar progress={progress} message={progressMessage} />}
      
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseModal}
        onDownload={downloadPDF}
        onHome={handleHome}
        quoteId={generatedQuoteId}
        hasDownloadPermission={hasPermission('download_quotes') || hasPermission('manage_quotes')}
      />

      {message && (
        <Alert className="border-yellow-400">
          <AlertDescription>{message}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column: Product Selection */}
        <Card className="border-t-4 border-t-yellow-400">
          <CardHeader>
            <CardTitle className="text-yellow-600">Select Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {products.map((product) => (
                <div key={product.product_id} className="flex items-center space-x-4 p-3 border rounded-lg hover:bg-slate-50">
                  <div className="w-16 h-16 bg-slate-200 rounded-lg overflow-hidden flex-shrink-0">
                    {product.image_url ? (
                      <img 
                        src={product.image_url?.startsWith('http') ? product.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${product.image_url}`}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingCart size={20} className="text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-grow">
                    <h4 className="font-semibold text-slate-800">{product.name}</h4>
                    <p className="text-sm text-slate-600">{product.brand} - ₹{product.price}</p>
                    <p className="text-xs text-slate-500 mt-1">{product.short_description}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Label>Qty:</Label>
                    <Input
                      type="number"
                      min="0"
                      max="999"
                      className="w-20"
                      value={selectedProducts.find(p => p.product_id === product.product_id)?.quantity || 0}
                      onChange={(e) => handleProductSelect(product, parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Right Column: Quote Form */}
        <Card className="border-t-4 border-t-yellow-400">
          <CardHeader>
            <CardTitle className="text-yellow-600">Quote Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={quoteData.company_name}
                    onChange={(e) => setQuoteData({ ...quoteData, company_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="quote_date">Quote Date (DD-MMM-YYYY)</Label>
                  <Input
                    id="quote_date"
                    value={quoteData.quote_date}
                    onChange={(e) => setQuoteData({ ...quoteData, quote_date: e.target.value })}
                    placeholder="e.g., 26-Aug-2025"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="client_email">Company Email</Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={quoteData.client_email}
                    onChange={(e) => setQuoteData({ ...quoteData, client_email: e.target.value })}
                    required
                  />
                </div>
				</div>

              <div>
                <Label htmlFor="client_address">Company Address</Label>
                <Textarea
                  id="client_address"
                  value={quoteData.client_address}
                  onChange={(e) => setQuoteData({ ...quoteData, client_address: e.target.value })}
                  required
                />
              </div>

              {/* Conditionally show admin fields */}
              {isAdmin && (
                <>
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={quoteData.subject}
                      onChange={(e) => setQuoteData({ ...quoteData, subject: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="salutation">Salutation</Label>
                    <Input
                      id="salutation"
                      value={quoteData.salutation}
                      onChange={(e) => setQuoteData({ ...quoteData, salutation: e.target.value })}
                      placeholder="Dear Sir/Madam"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote_description">Quote Description</Label>
                    <Textarea
                      id="quote_description"
                      value={quoteData.quote_description}
                      onChange={(e) => setQuoteData({ ...quoteData, quote_description: e.target.value })}
                      placeholder="Brief description about this quote..."
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="pf_costs">P&F Costs (₹)</Label>
                      <Input
                        id="pf_costs"
                        type="number"
                        step="100"
                        value={quoteData.pf_costs}
                        onChange={(e) => setQuoteData({ ...quoteData, pf_costs: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
					<div> {/* NEW FREIGHT CHARGES FIELD */}
                      <Label htmlFor="freight_charges">Freight Charges (₹)</Label>
                      <Input
                        id="freight_charges"
                        type="number"
                        step="100"
                        value={quoteData.freight_charges}
                        onChange={(e) => setQuoteData({ ...quoteData, freight_charges: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="tax_type">Tax Type</Label> {/* NEW TAX TYPE DROPDOWN */}
                      <select
                        id="tax_type"
                        value={quoteData.tax_type}
                        onChange={(e) => setQuoteData({ ...quoteData, tax_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      >
                        <option value="GST">GST</option>
                        <option value="IGST">IGST</option>
                        <option value="CGST">CGST</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="gst_percentage">{quoteData.tax_type} Percentage (%)</Label> {/* UPDATED LABEL */}
                      <Input
                        id="gst_percentage"
                        type="number"
                        step="1"
                        value={quoteData.gst_percentage}
                        onChange={(e) => setQuoteData({ ...quoteData, gst_percentage: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  {/* Template Manager */}
                  <div>
                    <Button 
                      type="button" 
                      onClick={() => setShowTemplateManager(true)}
                      className="mb-4"
                    >
                      <Save className="mr-2" size={16} />
                      Manage Templates
                    </Button>
                    
                    <TemplateManager 
                      isOpen={showTemplateManager} 
                      onOpenChange={setShowTemplateManager}
                      onSelectTemplate={handleTemplateSelect}
                    />
                  </div>

                  {/* Terms & Conditions */}
                  <div>
                    <Label htmlFor="terms_conditions">Terms & Conditions</Label>
                    <Textarea
                      id="terms_conditions"
                      value={quoteData.terms_conditions}
                      onChange={(e) => setQuoteData({ ...quoteData, terms_conditions: e.target.value })}
                      rows={6}
                      placeholder="Enter terms and conditions for this quote...
                    Example:
                    1. Prices FOR our office in Aligarh
                    2. Payment - 100% in Advance
                    3. Delivery within 7 days or less."
                      className="whitespace-pre-wrap font-mono text-sm"
                    />
                  </div>


                  {/* Signature and Bank Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border rounded-lg p-4">
                      <Label htmlFor="signature" className="block mb-2 font-semibold">Signature Area</Label>
                      <Textarea
                        id="signature"
                        value={quoteData.signature}
                        onChange={(e) => setQuoteData({ ...quoteData, signature: e.target.value })}
                        rows={3}
                        placeholder="Enter signature details...
                      Example:
                      For Crescent Traders
                      Tanveer Mustajab
                      (Propriter)"
                        className="border-0 focus:ring-0 whitespace-pre-wrap font-mono text-sm"
                      />
                    </div>
                    
                    <div className="border rounded-lg p-4">
                      <Label htmlFor="bank_details" className="block mb-2 font-semibold">Bank Details</Label>
                      <Textarea
                        id="bank_details"
                        value={quoteData.bank_details}
                        onChange={(e) => setQuoteData({ ...quoteData, bank_details: e.target.value })}
                        rows={4}
                        placeholder="Enter bank account details...
                      Example:
                      Bank Name: Canara Bank
                      Branch: Kela Nagar
                      Account: 2236556722342706
                      IFSC: CANBANK
                      Address: Dodhpur, Kela Nagar, Aligarh, U.P India."
                        className="border-0 focus:ring-0 whitespace-pre-wrap font-mono text-sm"
                      />
                    </div>
                  </div>
                </>
              )}

              <Button 
                type="submit" 
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-semibold"
                disabled={loading}
              >
                {loading ? 'Generating Quote...' : 'Generate Catalogue Quote'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Selected Products Summary */}
      {selectedProducts.length > 0 && (
        <Card className="border-t-4 border-t-yellow-400">
          <CardHeader>
            <CardTitle className="text-yellow-600">Quote Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                {selectedProducts.map((product) => (
                  <div key={product.product_id} className="flex justify-between items-center py-2 border-b">
                    <div>
                      <span className="font-semibold">{product.name}</span>
                      <span className="text-slate-600 ml-2">({product.brand})</span>
                      <p className="text-sm text-slate-500 mt-1">{product.short_description}</p>
                    </div>
                    <div className="text-right">
                      <div>₹{product.price} x {product.quantity}</div>
                      <div className="font-semibold">₹{(product.price * product.quantity).toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between">
                  <span>Items Total:</span>
                  <span>₹{itemsTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>P&F Costs:</span>
                  <span>₹{quoteData.pf_costs.toFixed(2)}</span>
                </div>
                <div className="flex justify-between"> {/* NEW FREIGHT CHARGES ROW */}
                  <span>Freight Charges:</span>
                  <span>₹{quoteData.freight_charges.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Total Before Tax:</span>
                  <span>₹{totalBeforeTax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{quoteData.tax_type} ({quoteData.gst_percentage}%):</span> {/* UPDATED LABEL */}
                  <span>₹{taxAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-yellow-600 border-t pt-2">
                  <span>Total After Tax:</span>
                  <span>₹{totalAfterTax.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Manual Quote Form Component
function ManualQuoteForm() {
  const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const { isAdmin, hasPermission } = useAuth();
  const [quoteData, setQuoteData] = useState({
    company_name: '',
    client_email: '',
    client_address: '',
    subject: '',
    salutation: '',
    quote_description: '',
    items: [{ product_name: '', description: '', price: 0, quantity: 1 }],
    pf_costs: 0,
    freight_charges: 0, // NEW FIELD
    tax_type: 'GST', // NEW FIELD
    gst_percentage: 18,
    quote_date: formatDate(new Date()),
    terms_conditions: '',
    signature: '',
    bank_details: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showAddToCatalogue, setShowAddToCatalogue] = useState({});
  const [generatedQuoteId, setGeneratedQuoteId] = useState('');
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  
  // New states for progress and modal
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const addItem = () => {
    setQuoteData({
      ...quoteData,
      items: [...quoteData.items, { product_name: '', description: '', price: 0, quantity: 1 }]
    });
  };

  const removeItem = (index) => {
    const newItems = quoteData.items.filter((_, i) => i !== index);
    setQuoteData({ ...quoteData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...quoteData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setQuoteData({ ...quoteData, items: newItems });
  };

  const addItemToCatalogue = async (item, index) => {
    try {
      const response = await apiClient.post(`/products/from-manual`, {
        name: item.product_name,
        brand: item.brand || 'Manual Entry',
        price: item.price,
        description: item.description,
        short_description: item.short_description || item.description,
        long_description: item.long_description || item.description
      }, {
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}` 
        }
      });

      setMessage(`Item "${item.product_name}" added to catalogue successfully!`);
      setShowAddToCatalogue({ ...showAddToCatalogue, [index]: false });
    } catch (error) {
      setMessage('Error adding item to catalogue.');
    }
  };

  const calculateTotals = () => {
    const itemsTotal = quoteData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalBeforeTax = itemsTotal + quoteData.pf_costs + quoteData.freight_charges; // UPDATED
    const taxAmount = totalBeforeTax * (quoteData.gst_percentage / 100);
    const totalAfterTax = totalBeforeTax + taxAmount;

    return { itemsTotal, totalBeforeTax, taxAmount, totalAfterTax };
  };

  const simulateProgress = () => {
    setProgress(0);
    const intervals = [
      { time: 500, progress: 20, message: "Preparing quote data..." },
      { time: 1000, progress: 40, message: "Generating PDF document..." },
      { time: 1500, progress: 70, message: "Sending confirmation email..." },
      { time: 2000, progress: 85, message: "Sending approval request..." },
      { time: 500, progress: 100, message: "Finalizing quote..." }
    ];

    intervals.forEach((interval, index) => {
      setTimeout(() => {
        setProgress(interval.progress);
        setProgressMessage(interval.message);
        
        if (index === intervals.length - 1) {
          setTimeout(() => {
            setShowProgress(false);
            setShowSuccessModal(true);
          }, 500);
        }
      }, interval.time);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate items
    for (let i = 0; i < quoteData.items.length; i++) {
      const item = quoteData.items[i];
      if (!item.product_name || !item.description || item.price <= 0 || item.quantity <= 0) {
        setMessage(`Please fill all fields for item ${i + 1} correctly.`);
        return;
      }
    }
    
    setLoading(true);
    setShowProgress(true);
    simulateProgress();
    
    try {
      const formData = new FormData();
      formData.append('company_name', quoteData.company_name);
      formData.append('client_email', quoteData.client_email);
      formData.append('client_address', quoteData.client_address);
      formData.append('subject', quoteData.subject);
      formData.append('salutation', quoteData.salutation);
      formData.append('quote_description', quoteData.quote_description);
      formData.append('pf_costs', quoteData.pf_costs);
	  formData.append('freight_charges', quoteData.freight_charges); // ADD THIS LINE
      formData.append('tax_type', quoteData.tax_type);
      formData.append('gst_percentage', quoteData.gst_percentage);
      formData.append('quote_date', quoteData.quote_date);
      formData.append('terms_conditions', quoteData.terms_conditions);
      formData.append('signature', quoteData.signature);
      formData.append('bank_details', quoteData.bank_details);
      formData.append('manual_items', JSON.stringify(quoteData.items));

      const response = await apiClient.post(`/quotes/manual`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setMessage('Manual quote generated successfully! Approval email sent.');
      setGeneratedQuoteId(response.data.quote_id);
      
      // Reset form
      setQuoteData({
        company_name: '',
        client_email: '',
        client_address: '',
        subject: '',
        salutation: '',
        quote_description: '',
        items: [{ product_name: '', description: '', price: 0, quantity: 1 }],
        pf_costs: 0,
		freight_charges: 0, // RESET THIS TOO
        tax_type: 'GST',
        gst_percentage: 18,
        terms_conditions: '',
        signature: '',
        bank_details: ''
      });
    } catch (error) {
      setMessage('Error generating quote. Please try again.');
      setShowProgress(false);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!generatedQuoteId) return;
    
    try {
      const response = await apiClient.get(`/quotes/${generatedQuoteId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote_${generatedQuoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Close modal after download
      setShowSuccessModal(false);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleHome = () => {
    // Navigate to home/dashboard
    window.location.href = '/dashboard'; // Adjust to your home route
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const handleTemplateSelect = (template) => {
    setQuoteData({
      ...quoteData,
      terms_conditions: template.terms_conditions,
      signature: template.signature,
      bank_details: template.bank_details
    });
    setShowTemplateManager(false);
  };

  const { itemsTotal, totalBeforeTax, taxAmount, totalAfterTax } = calculateTotals();

  return (
    <Card className="border-t-4 border-t-yellow-400">
      <CardContent className="p-8">
        {/* Progress Bar */}
        {showProgress && <ProgressBar progress={progress} message={progressMessage} />}
        
        {/* Success Modal */}
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={handleCloseModal}
          onDownload={downloadPDF}
          onHome={handleHome}
          quoteId={generatedQuoteId}
          hasDownloadPermission={hasPermission('download_quotes') || hasPermission('manage_quotes')}
        />

        {message && (
          <Alert className="mb-6 border-yellow-400">
            <AlertDescription>
              {message}
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={quoteData.company_name}
                onChange={(e) => setQuoteData({ ...quoteData, company_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="client_email">Company Email</Label>
              <Input
                id="client_email"
                type="email"
                value={quoteData.client_email}
                onChange={(e) => setQuoteData({ ...quoteData, client_email: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="client_address">Company Address</Label>
            <Textarea
              id="client_address"
              value={quoteData.client_address}
              onChange={(e) => setQuoteData({ ...quoteData, client_address: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="quote_date">Quote Date (DD-MMM-YYYY)</Label>
            <Input
              id="quote_date"
              value={quoteData.quote_date}
              onChange={(e) => {
                // Basic validation to ensure proper format
                const value = e.target.value;
                if (value.length <= 11) { // DD-MMM-YYYY is 11 characters
                  setQuoteData({ ...quoteData, quote_date: value });
                }
              }}
              onBlur={(e) => {
                // Try to parse and format the date when user leaves the field
                try {
                  const parts = e.target.value.split('-');
                  if (parts.length === 3) {
                    const day = parseInt(parts[0]);
                    const month = parts[1];
                    const year = parseInt(parts[2]);
                    
                    // Convert month name to number
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const monthIndex = monthNames.findIndex(m => m.toLowerCase() === month.toLowerCase());
                    
                    if (monthIndex !== -1 && !isNaN(day) && !isNaN(year)) {
                      const date = new Date(year, monthIndex, day);
                      setQuoteData({ ...quoteData, quote_date: formatDate(date) });
                    }
                  }
                } catch (error) {
                  console.error('Error parsing date:', error);
                  // Reset to today's date if parsing fails
                  setQuoteData({ ...quoteData, quote_date: formatDate(new Date()) });
                }
              }}
              placeholder="DD-MMM-YYYY (e.g., 28-Aug-2025)"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={quoteData.subject}
                onChange={(e) => setQuoteData({ ...quoteData, subject: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="salutation">Salutation</Label>
              <Input
                id="salutation"
                value={quoteData.salutation}
                onChange={(e) => setQuoteData({ ...quoteData, salutation: e.target.value })}
                placeholder="Dear Sir/Madam"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="quote_description">Quote Description</Label>
            <Textarea
              id="quote_description"
              value={quoteData.quote_description}
              onChange={(e) => setQuoteData({ ...quoteData, quote_description: e.target.value })}
              placeholder="Brief description about this quote..."
              rows={3}
            />
          </div>

          {/* Items Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Quote Items</h3>
              <Button type="button" onClick={addItem} className="bg-yellow-400 hover:bg-yellow-500 text-slate-900">
                <Plus className="mr-2" size={16} />
                Add Item
              </Button>
            </div>

            {quoteData.items.map((item, index) => (
              <Card key={index} className="mb-4 border-l-4 border-l-slate-300">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-4">
                    <h4 className="font-medium text-slate-800">Item {index + 1}</h4>
                    <div className="flex space-x-2">
                      {item.product_name && item.description && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAddToCatalogue({ ...showAddToCatalogue, [index]: !showAddToCatalogue[index] })}
                          className="text-yellow-600 border-yellow-400 hover:bg-yellow-50"
                        >
                          <Package size={16} className="mr-1" />
                          Add to Catalogue
                        </Button>
                      )}
                      {quoteData.items.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {showAddToCatalogue[index] && (
                    <div className="mb-4 p-3 bg-yellow-50 rounded border-l-4 border-l-yellow-400">
                      <p className="text-sm text-slate-700 mb-2">Add this item to catalogue for future use?</p>
                      <div className="space-x-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => addItemToCatalogue(item, index)}
                          className="bg-yellow-400 hover:bg-yellow-500 text-slate-900"
                        >
                          Yes, Add to Catalogue
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAddToCatalogue({ ...showAddToCatalogue, [index]: false })}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Product Name</Label>
                      <Input
                        value={item.product_name}
                        onChange={(e) => updateItem(index, 'product_name', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        required
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Price (₹)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={item.price}
                        onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                        required
                      />
                    </div>
                    <div>
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        required
                      />
                    </div>
                  </div>
                  <div className="mt-2 text-right">
                    <span className="font-semibold text-slate-700">Amount: ₹{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Costs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4"> {/* UPDATED to 4 columns */}
            <div>
              <Label htmlFor="pf_costs">P&F Costs (₹)</Label>
              <Input
                id="pf_costs"
                type="number"
                step="100"
                value={quoteData.pf_costs}
                onChange={(e) => setQuoteData({ ...quoteData, pf_costs: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div> {/* NEW FREIGHT CHARGES FIELD */}
              <Label htmlFor="freight_charges">Freight Charges (₹)</Label>
              <Input
                id="freight_charges"
                type="number"
                step="100"
                value={quoteData.freight_charges}
                onChange={(e) => setQuoteData({ ...quoteData, freight_charges: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div> {/* NEW TAX TYPE DROPDOWN */}
              <Label htmlFor="tax_type">Tax Type</Label>
              <select
                id="tax_type"
                value={quoteData.tax_type}
                onChange={(e) => setQuoteData({ ...quoteData, tax_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
              >
                <option value="GST">GST</option>
                <option value="IGST">IGST</option>
                <option value="CGST">CGST</option>
              </select>
            </div>
            <div>
              <Label htmlFor="gst_percentage">{quoteData.tax_type} Percentage (%)</Label> {/* UPDATED LABEL */}
              <Input
                id="gst_percentage"
                type="number"
                step="1"
                value={quoteData.gst_percentage}
                onChange={(e) => setQuoteData({ ...quoteData, gst_percentage: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          {/* Admin-only fields */}
          {isAdmin && (
            <>
              {/* Template Manager */}
              <div>
                <Button 
                  type="button" 
                  onClick={() => setShowTemplateManager(true)}
                  className="mb-4"
                >
                  <Save className="mr-2" size={16} />
                  Manage Templates
                </Button>
                
                <TemplateManager 
                  isOpen={showTemplateManager} 
                  onOpenChange={setShowTemplateManager}
                  onSelectTemplate={handleTemplateSelect}
                />
              </div>

              {/* Terms & Conditions */}
              <div>
                <Label htmlFor="terms_conditions">Terms & Conditions</Label>
                <Textarea
                  id="terms_conditions"
                  value={quoteData.terms_conditions}
                  onChange={(e) => setQuoteData({ ...quoteData, terms_conditions: e.target.value })}
                  rows={4}
                  placeholder="Enter terms and conditions for this quote..."
                />
              </div>

              {/* Signature and Bank Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border rounded-lg p-4">
                  <Label htmlFor="signature" className="block mb-2 font-semibold">Signature Area</Label>
                  <Textarea
                    id="signature"
                    value={quoteData.signature}
                    onChange={(e) => setQuoteData({ ...quoteData, signature: e.target.value })}
                    rows={3}
                    placeholder="Enter signature details..."
                    className="border-0 focus:ring-0"
                  />
                </div>
                
                <div className="border rounded-lg p-4">
                  <Label htmlFor="bank_details" className="block mb-2 font-semibold">Bank Details</Label>
                  <Textarea
                    id="bank_details"
                    value={quoteData.bank_details}
                    onChange={(e) => setQuoteData({ ...quoteData, bank_details: e.target.value })}
                    rows={3}
                    placeholder="Enter bank account details..."
                    className="border-0 focus:ring-0"
                  />
                </div>
              </div>
            </>
          )}

          {/* Totals Summary */}
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-4 text-slate-800">Quote Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Items Total:</span>
                  <span>₹{itemsTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>P&F Costs:</span>
                  <span>₹{quoteData.pf_costs.toFixed(2)}</span>
                </div>
                <div className="flex justify-between"> {/* NEW FREIGHT CHARGES ROW */}
                  <span>Freight Charges:</span>
                  <span>₹{quoteData.freight_charges.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Total Before Tax:</span>
                  <span>₹{totalBeforeTax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{quoteData.tax_type} ({quoteData.gst_percentage}%):</span> {/* UPDATED LABEL */}
                  <span>₹{taxAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-yellow-600 border-t pt-2">
                  <span>Total After Tax:</span>
                  <span>₹{totalAfterTax.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex space-x-4">
            <Button 
              type="submit" 
              className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-slate-900 text-lg py-3 font-semibold"
              disabled={loading || quoteData.items.length === 0}
            >
              {loading ? 'Generating Quote...' : 'Generate Manual Quote'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
function IndividualItemQuoteForm() {
  const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const createEmptyItem = () => ({
    item_name: '',
    item_description: '',
    price: '',
    quantity: 1,
    pf_costs: '',
    freight_charges: '',
    tax_type: 'GST',
    gst_percentage: 18,
    terms_conditions: ''
  });


  // Main quote-level data (no item fields here)
  const [quoteData, setQuoteData] = useState({
    company_name: '',
    client_email: '',
    client_address: '',
    subject: '',
    salutation: '',
    quote_description: '',
    pf_costs: 0,
    freight_charges: 0,
    tax_type: 'GST',
    gst_percentage: 18,
    quote_date: formatDate(new Date()),
    terms_conditions: '',
    signature: '',
    bank_details: ''
  });

  // Per-item quote data (each item carries its own PF, freight, tax, T&C)
  const [items, setItems] = useState([createEmptyItem()]);


  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const handleHome = () => {
    window.location.href = "/dashboard";
  };
  const [generatedQuoteId, setGeneratedQuoteId] = useState('');
    // Template managers: per-item T&C and footer (signature + bank)
  const [activeTermsTemplateIndex, setActiveTermsTemplateIndex] = useState(null);
  const [showFooterTemplateManager, setShowFooterTemplateManager] = useState(false);
  const { isAdmin, hasPermission } = useAuth();


  const simulateProgress = () => {
    setProgress(0);
    setProgressMessage('Preparing to generate quote...');

    setTimeout(() => {
      setProgress(30);
      setProgressMessage('Calculating totals and taxes...');
    }, 500);

    setTimeout(() => {
      setProgress(60);
      setProgressMessage('Generating professional quote document...');
    }, 1200);

    setTimeout(() => {
      setProgress(90);
      setProgressMessage('Finalizing and saving quote...');
    }, 2000);
  };

  const handleAddItem = () => {
    setItems((prev) => [...prev, createEmptyItem()]);
    setMessage('');
  };


  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clean items: only keep those with valid name + positive price/qty
    const finalItems = items
      .map((item) => {
        const price = parseFloat(item.price) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const pf = parseFloat(item.pf_costs) || 0;
        const freight = parseFloat(item.freight_charges) || 0;
        const gstPct = parseFloat(item.gst_percentage) || 0;

        const totalBeforeTax = price * quantity + pf + freight;
        const taxAmount = totalBeforeTax * (gstPct / 100);
        const totalAfterTax = totalBeforeTax + taxAmount;

        return {
          ...item,
          price,
          quantity,
          pf_costs: pf,
          freight_charges: freight,
          gst_percentage: gstPct,
          total_before_tax: totalBeforeTax,
          tax_amount: taxAmount,
          total_after_tax: totalAfterTax
        };
      })
      .filter((item) => item.item_name && item.price > 0 && item.quantity > 0);

    if (!finalItems.length) {
      setMessage('Please add at least one valid item (name, price > 0, quantity > 0).');
      return;
    }

    try {
      setLoading(true);
      setShowProgress(true);
      setProgress(10);
      setProgressMessage('Preparing quote data...');

      const formData = new FormData();
      formData.append('company_name', quoteData.company_name);
	  formData.append("quote_date", quoteData.quote_date);
      formData.append('client_email', quoteData.client_email);
      formData.append('client_address', quoteData.client_address);
      formData.append('subject', quoteData.subject);
      formData.append('salutation', quoteData.salutation);
      formData.append('quote_description', quoteData.quote_description || '');
      formData.append('quote_date', quoteData.quote_date);
      formData.append('terms_conditions', quoteData.terms_conditions || '');
      formData.append('signature', quoteData.signature || '');
      formData.append('bank_details', quoteData.bank_details || '');

      // Individual per-item payload
      const payloadItems = finalItems.map((item) => ({
        product_name: item.item_name,
        description: item.item_description,
        price: item.price,
        quantity: item.quantity,
        pf_costs: item.pf_costs,
        freight_charges: item.freight_charges,
        tax_type: item.tax_type,
        gst_percentage: item.gst_percentage,
        total_before_tax: item.total_before_tax,
        tax_amount: item.tax_amount,
        total_after_tax: item.total_after_tax,
        terms_conditions: item.terms_conditions
      }));

      formData.append('individual_items', JSON.stringify(payloadItems));

      setProgress(40);
      setProgressMessage('Submitting quote to server...');

      const response = await apiClient.post(`/quotes/individual`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setProgress(80);
      setProgressMessage('Finalizing quote and generating PDF...');

      if (response.data && response.data.quote_id) {
        setGeneratedQuoteId(response.data.quote_id);
      }

      setTimeout(() => {
        setProgress(100);
        setProgressMessage('Quote generated successfully!');
        setShowSuccessModal(true);
      }, 2500);

      setMessage('Individual item quote generated successfully!');
    } catch (error) {
      console.error('Error generating individual quote:', error);
      setMessage(
        error?.response?.data?.detail || 'Failed to generate individual item quote. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!generatedQuoteId) return;

    try {
      const response = await apiClient.get(`/quotes/${generatedQuoteId}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quote_${generatedQuoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setShowSuccessModal(false);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  return (
    <Card className="border-t-4 border-t-yellow-400">
      <CardContent className="p-8">
        {/* Progress Bar */}
        {showProgress && (
          <div className="mb-4">
            <ProgressBar progress={progress} message={progressMessage} />
          </div>
        )}

        {/* Success Modal */}
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          onDownload={downloadPDF}
          onHome={handleHome}
          quoteId={generatedQuoteId}
          hasDownloadPermission={hasPermission('download_quotes') || hasPermission('manage_quotes')}
        />


        {message && (
          <Alert className="mb-6 border-yellow-400">
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Client Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={quoteData.company_name}
                onChange={(e) => setQuoteData({ ...quoteData, company_name: e.target.value })}
                required
              />
            </div>
			<div>
			  <Label htmlFor="quote_date">Quote Date</Label>
			  <Input
				id="quote_date"
				type="date"
				value={(() => {
				  try {
					const [day, mon, year] = quoteData.quote_date.split('-');
					const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
					const mIndex = monthNames.findIndex(m => m.toLowerCase() === mon.toLowerCase());
					if (mIndex === -1) return '';

					// Output ISO YYYY-MM-DD WITHOUT timezone side effects
					return `${year}-${String(mIndex + 1).padStart(2,'0')}-${day}`;
				  } catch {
					return '';
				  }
				})()}
				onChange={(e) => {
				  const iso = e.target.value; // YYYY-MM-DD

				  if (!iso) return;

				  const [yy, mm, dd] = iso.split("-").map(Number);

				  // Create DATE WITHOUT timezone shift (pure UTC)
				  const d = new Date(Date.UTC(yy, mm - 1, dd));

				  const day = String(dd).padStart(2, "0");
				  const month = d.toLocaleString('default', { month: 'short' });
				  const year = yy;

				  setQuoteData({
					...quoteData,
					quote_date: `${day}-${month}-${year}`
				  });
				}}
				required
			  />
			</div>

            <div>
              <Label htmlFor="client_email">Company Email</Label>
              <Input
                id="client_email"
                type="email"
                value={quoteData.client_email}
                onChange={(e) => setQuoteData({ ...quoteData, client_email: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="client_address">Company Address</Label>
            <Textarea
              id="client_address"
              value={quoteData.client_address}
              onChange={(e) => setQuoteData({ ...quoteData, client_address: e.target.value })}
              rows={3}
            />
          </div>

          {/* Quote Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={quoteData.subject}
                onChange={(e) => setQuoteData({ ...quoteData, subject: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="salutation">Salutation</Label>
              <Input
                id="salutation"
                value={quoteData.salutation}
                onChange={(e) => setQuoteData({ ...quoteData, salutation: e.target.value })}
                placeholder="Dear Sir/Madam"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="quote_description">Quote Description</Label>
            <Textarea
              id="quote_description"
              value={quoteData.quote_description}
              onChange={(e) => setQuoteData({ ...quoteData, quote_description: e.target.value })}
              placeholder="Brief description about this quote..."
              rows={3}
            />
          </div>

          {/* Items Section – each item has its own totals, no grand total */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Quote Items</h3>
              <Button
                type="button"
                onClick={handleAddItem}
                className="bg-yellow-400 hover:bg-yellow-500 text-slate-900"
              >
                <Plus className="mr-2" size={16} />
                Add Item
              </Button>
            </div>

            {items.map((item, index) => {
              const price = parseFloat(item.price) || 0;
              const qty = parseFloat(item.quantity) || 0;
              const pf = parseFloat(item.pf_costs) || 0;
              const freight = parseFloat(item.freight_charges) || 0;
              const gstPct = parseFloat(item.gst_percentage) || 0;

              const totalBeforeTax = price * qty + pf + freight;
              const taxAmount = totalBeforeTax * (gstPct / 100);
              const totalAfterTax = totalBeforeTax + taxAmount;

              return (
                <Card key={index} className="mb-4 border-l-4 border-l-slate-300">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-start justify-between">
                      <h4 className="font-medium text-slate-800">Item {index + 1}</h4>
                      <div className="flex space-x-2">
                        {items.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-500 hover:text-red-700 border-red-300 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Product Name</Label>
                        <Input
                          value={item.item_name}
                          onChange={(e) => {
                            const updated = [...items];
                            updated[index] = { ...updated[index], item_name: e.target.value };
                            setItems(updated);
                          }}
                          required
                        />
                      </div>
                      <div>
                        <Label>Product Description</Label>
                        <Textarea
                          value={item.item_description}
                          onChange={(e) => {
                            const updated = [...items];
                            updated[index] = { ...updated[index], item_description: e.target.value };
                            setItems(updated);
                          }}
                          rows={3}
                          required
                        />
                      </div>

                      <div>
                        <Label>Price (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => {
                            const updated = [...items];
                            updated[index] = {
                              ...updated[index],
                              price: e.target.value
                            };
                            setItems(updated);
                          }}
                          required
                        />
                      </div>

                      <div>
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          step="1"
                          value={item.quantity}
                          onChange={(e) => {
                            const updated = [...items];
                            updated[index] = {
                              ...updated[index],
                              quantity: e.target.value
                            };
                            setItems(updated);
                          }}
                          required
                        />
                      </div>

                      <div>
                        <Label>P&amp;F Costs (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.pf_costs}
                          onChange={(e) => {
                            const updated = [...items];
                            updated[index] = {
                              ...updated[index],
                              pf_costs: e.target.value
                            };
                            setItems(updated);
                          }}
                        />
                      </div>

                      <div>
                        <Label>Freight Charges (₹)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.freight_charges}
                          onChange={(e) => {
                            const updated = [...items];
                            updated[index] = {
                              ...updated[index],
                              freight_charges: e.target.value
                            };
                            setItems(updated);
                          }}
                        />
                      </div>

                      <div>
                        <Label>Tax Type</Label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          value={item.tax_type}
                          onChange={(e) => {
                            const updated = [...items];
                            updated[index] = {
                              ...updated[index],
                              tax_type: e.target.value
                            };
                            setItems(updated);
                          }}
                        >
                          <option value="GST">GST</option>
                          <option value="IGST">IGST</option>
                          <option value="CGST">CGST</option>
                        </select>
                      </div>

                      <div>
                        <Label>GST Percentage (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={item.gst_percentage}
                          onChange={(e) => {
                            const updated = [...items];
                            updated[index] = {
                              ...updated[index],
                              gst_percentage: e.target.value
                            };
                            setItems(updated);
                          }}
                        />
                      </div>
                    </div>

                    {/* Item Totals (no grand total) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <Label>Total Price Before Tax (₹)</Label>
                        <Input value={totalBeforeTax.toFixed(2)} readOnly />
                      </div>
                      <div>
                        <Label>Total Price After Tax (₹)</Label>
                        <Input value={totalAfterTax.toFixed(2)} readOnly />
                      </div>
                    </div>
					
					{/* Add to Catalogue Button */}
					{item.item_name && item.item_description && (
					  <Button
						type="button"
						variant="outline"
						size="sm"
						className="text-yellow-600 border-yellow-400 hover:bg-yellow-50 mb-3"
						onClick={async () => {
						  try {
							await apiClient.post("/products/from-manual", {
							  name: item.item_name,
							  brand: "Manual Entry",
							  price: item.price,
							  description: item.item_description,
							  short_description: item.item_description,
							  long_description: item.item_description
							}, {
							  headers: {
								"Content-Type": "application/json",
								Authorization: `Bearer ${localStorage.getItem("token")}`
							  }
							});

							setMessage(`Item "${item.item_name}" added to catalogue successfully!`);
						  } catch (err) {
							setMessage("Error adding item to catalogue.");
						  }
						}}
					  >
						<Package size={16} className="mr-1" />
						Add to Catalogue
					  </Button>
					)}


                    {/* Item-specific Terms & Conditions + Template button */}
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className="font-semibold">Terms &amp; Conditions</Label>
                        {isAdmin && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-yellow-600 border-yellow-400 hover:bg-yellow-50"
                            onClick={() => setActiveTermsTemplateIndex(index)}
                          >
                            <Save className="mr-1" size={14} />
                            Apply Terms Template
                          </Button>
                        )}
                      </div>
                      <Textarea
                        value={item.terms_conditions}
                        onChange={(e) => {
                          const updated = [...items];
                          updated[index] = {
                            ...updated[index],
                            terms_conditions: e.target.value
                          };
                          setItems(updated);
                        }}
                        rows={4}
                        placeholder="Enter terms and conditions for this item..."
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>


          {/* Admin-only footer fields */}
          {isAdmin && (
            <>
              {/* Footer Template (Signature + Bank) */}
              <div className="mb-4">
                <Button
                  type="button"
                  onClick={() => setShowFooterTemplateManager(true)}
                  className="bg-yellow-400 hover:bg-yellow-500 text-slate-900"
                >
                  <Save className="mr-2" size={16} />
                  Manage Signature &amp; Bank Templates
                </Button>

                {showFooterTemplateManager && (
                  <TemplateManager
                    isOpen={showFooterTemplateManager}
                    onOpenChange={setShowFooterTemplateManager}
                    mode="footer"
                    onSelectTemplate={(template) => {
                      setQuoteData((prev) => ({
                        ...prev,
                        signature: template.signature || prev.signature,
                        bank_details: template.bank_details || prev.bank_details
                      }));
                      setShowFooterTemplateManager(false);
                    }}
                  />
                )}
              </div>


              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border rounded-lg p-4">
                  <Label htmlFor="signature" className="block mb-2 font-semibold">
                    Signature Area
                  </Label>
                  <Textarea
                    id="signature"
                    value={quoteData.signature}
                    onChange={(e) => setQuoteData({ ...quoteData, signature: e.target.value })}
                    rows={3}
                    placeholder="Add signature text or notes..."
                  />
                </div>
                <div className="border rounded-lg p-4">
                  <Label htmlFor="bank_details" className="block mb-2 font-semibold">
                    Bank Details
                  </Label>
                  <Textarea
                    id="bank_details"
                    value={quoteData.bank_details}
                    onChange={(e) => setQuoteData({ ...quoteData, bank_details: e.target.value })}
                    rows={3}
                    placeholder="Add bank details that will appear on the quote..."
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 px-6"
              disabled={loading}
            >
              {loading ? 'Generating Quote...' : 'Generate Individual Item Quote'}
            </Button>
          </div>
        {/* Terms-only Template Manager, for item-specific T&Cs */}
        {isAdmin && activeTermsTemplateIndex !== null && (
          <TemplateManager
            isOpen={activeTermsTemplateIndex !== null}
            onOpenChange={(open) => {
              if (!open) setActiveTermsTemplateIndex(null);
            }}
            mode="terms"
            onSelectTemplate={(template) => {
              const updated = [...items];
              const idx = activeTermsTemplateIndex;
              if (idx !== null && updated[idx]) {
                updated[idx] = {
                  ...updated[idx],
                  terms_conditions: template.terms_conditions || ''
                };
                setItems(updated);
              }
              setActiveTermsTemplateIndex(null);
            }}
          />
        )}

        </form>
      </CardContent>
    </Card>
  );
}


export default QuotePage;